import { useCallback, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  GetSocialMediaPostsDocument,
  GetSocialMediaPostDocument,
  CreateSocialMediaPostsDocument,
  UpdateSocialMediaPostsDocument,
  DeleteSocialMediaPostsDocument,
  type GetSocialMediaPostsQuery,
  type GetSocialMediaPostQuery,
  type CreateSocialMediaPostsMutation,
  type UpdateSocialMediaPostsMutation,
  type DeleteSocialMediaPostsMutation,
  type Social_Media_Posts,
  type Social_Media_PostsUpdateInput,
} from "@/src/gql/graphql";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";
import {
  EMPTY_INFINITE_RESULT,
  useStableMutationResult,
} from "@/lib/hooks/api/shared/stable-entity-hook-results";
import { serializeGraphqlInput } from "@/lib/graphql/serialize-input";
// Writes go through pg_graphql, not an API route, so the server-side
// mapToUserFacingError never sees them. See mutation-error.ts.
import { crmMutationError } from "@/lib/utils/crm/mutation-error";

/**
 * Social media posts entity hooks — modeled on `contracts-graphql.ts`.
 *
 * Apollo + usePaginatedCollection (cache-and-network) + serializeGraphqlInput.
 * The SocialMediaPostFields fragment selects every displayed column, so the
 * codegen result node carries fragment refs — cast the resolved node to
 * `Social_Media_Posts` at the read boundary (same pattern as contracts).
 *
 * `youtube`/`results` are JSON columns; `serializeGraphqlInput("social_media_posts", …)`
 * handles the BigFloat/JSON scalar serialization required for pg_graphql mutations.
 */

/** Drop Apollo cache metadata (`__typename`, fragment-ref keys, relations) before a mutation. */
function cleanSocialMediaPostInput(
  input: Partial<Social_Media_Posts>
): Social_Media_PostsUpdateInput {
  const entries = Object.entries(input).filter(
    ([key]) =>
      !key.startsWith("__") &&
      key !== "id" &&
      key !== "nodeId" &&
      key !== "companies" &&
      key !== "contacts"
  );
  return Object.fromEntries(entries);
}

/**
 * Module-level so `usePaginatedCollection`'s result memo has stable deps — an
 * inline arrow allocated per render would make that memo a no-op.
 */
const selectSocialMediaPostsConnection = (data: GetSocialMediaPostsQuery) => data.social_media_postsCollection;
const mapSocialMediaPostNode = (node: unknown): Social_Media_Posts => node as Social_Media_Posts;

/**
 * Allocated ONCE — the object is a dependency of `useBuildActionContext`'s memo
 * and closes over nothing render-scoped (see `contacts-graphql.ts`).
 */
const SOCIAL_MEDIA_POST_ENTITY_HOOKS: EntityHooks<Social_Media_Posts> = {
  queryDocument: GetSocialMediaPostsDocument,

  usePaginated: (params?: Record<string, unknown>) =>
    // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
    usePaginatedCollection(GetSocialMediaPostsDocument, params, selectSocialMediaPostsConnection, mapSocialMediaPostNode),

  useInfinite: () => EMPTY_INFINITE_RESULT,

  useSingle: (id: string, options?: { enabled?: boolean }) => {
    const result = useQuery<GetSocialMediaPostQuery>(GetSocialMediaPostDocument, {
      variables: { id },
      errorPolicy: "all",
      skip: !id || (options && options.enabled === false),
    });
    return useMemo(() => {
      const node = result.data?.social_media_postsCollection?.edges?.[0]?.node;
      return {
        data: node ? (node as unknown as Social_Media_Posts) : undefined,
        isLoading: result.loading,
        error: result.error || null,
        refetch: result.refetch,
      };
    }, [result]);
  },

  useCreate: () => {
    const [mutate, result] = useMutation<CreateSocialMediaPostsMutation>(CreateSocialMediaPostsDocument);
    const mutateAsync = useCallback(
      async (input: Partial<Social_Media_Posts>) => {
        const cleanInput = serializeGraphqlInput("social_media_posts", cleanSocialMediaPostInput(input));
        const { data, error } = await mutate({
          variables: { input: cleanInput },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't create the social media post.");
        const created = data?.insertIntosocial_media_postsCollection?.records?.[0];
        if (!created) throw new Error("Couldn't create the social media post.");
        return created as unknown as Social_Media_Posts;
      },
      [mutate]
    );
    return useStableMutationResult(mutateAsync, result.loading);
  },

  useUpdate: () => {
    const [mutate, result] = useMutation<UpdateSocialMediaPostsMutation>(UpdateSocialMediaPostsDocument);

    const mutateAsync = useCallback(
      async ({ id, ...input }: { id: string } & Partial<Social_Media_Posts>) => {
        const cleanInput = serializeGraphqlInput("social_media_posts", cleanSocialMediaPostInput(input));
        const { data, error } = await mutate({
          variables: { id, input: cleanInput },
          errorPolicy: "all",
        });

        if (error) throw crmMutationError(error, "Couldn't update the social media post.");

        const updated = data?.updatesocial_media_postsCollection?.records?.[0];
        if (!updated) throw new Error("Couldn't update the social media post.");
        return updated as unknown as Social_Media_Posts;
      },
      [mutate]
    );

    return useStableMutationResult(mutateAsync, result.loading);
  },

  useDelete: () => {
    const [mutate, result] = useMutation<DeleteSocialMediaPostsMutation>(DeleteSocialMediaPostsDocument);
    const mutateAsync = useCallback(
      async (id: string) => {
        const { data, error } = await mutate({
          variables: { id },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't delete the social media post.");
        const deletedId = data?.deleteFromsocial_media_postsCollection?.records?.[0]?.id;
        if (!deletedId) throw new Error("Couldn't delete the social media post.");
        return deletedId;
      },
      [mutate]
    );
    return useStableMutationResult(mutateAsync, result.loading);
  },
};

export const useSocialMediaPostHooks = (): EntityHooks<Social_Media_Posts> => SOCIAL_MEDIA_POST_ENTITY_HOOKS;
